import React, { useEffect, useState } from "react"
import * as Yup from "yup"
import { useFormik } from "formik"
import withRouter from "components/Common/withRouter"
import PropTypes from "prop-types"
import {
  Card,
  CardBody,
  Form,
  Row,
  Col,
  Label,
  Input,
  FormFeedback,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import Spinners from "components/Common/Spinner"
import {
  addNewGoodServicesSubHeadMaster,
  getGoodServicesSubHeadMasterDetail,
  updateGoodServicesSubHeadMaster,
  deleteGoodServicesSubHeadMaster,
  getGoodServicesSubHeadMasterList,
} from "store/actions"
import {
  getAllHeadList,
  getUsersAndGoodsServices,
} from "helpers/fakebackend_helper"

const NewGoodServicesSubHeadMaster = props => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  
  const [headList, setHeadList] = useState([])
  useEffect(() => {
    // Define and execute data fetching functions
    const fetchHeadData = async () => {
      try {
        const response = await getAllHeadList()
        setHeadList(response.data)
      } catch (error) {
        console.error("Error fetching subhead:", error)
      }
    }

    // Call both fetch functions
    fetchHeadData()
  }, [])

  // Selectors
  const headsState = state =>
    state.goodServicesSubHeadMaster.goodServicesSubHead.data
  const goodsServicesState = state =>
    state.goodServicesSubHeadMaster.goodServicesSubHead.data
  const detailState = state =>
    state.goodServicesSubHeadMaster.goodServicesSubHeadDetail.data

  const loadingState = state => state.goodServicesSubHeadMaster.loading

  const componentProperties = createSelector(
    [headsState, goodsServicesState, detailState, loadingState],
    (heads, goodsServices, detail, loading) => ({
      heads,
      goodsServices,
      detail,
      loading,
    })
  )
  
  const handleHeadChange = (e) => {
    const selectedHeadId = e.target.value
    const selectedHead = headList.find(item => item.id === Number(selectedHeadId))
    validation.setFieldValue("good_service_id", selectedHead.good_service_id)
    validation.handleChange(e)
  }
  
  const { heads, goodsServices, detail, loading } =
    useSelector(componentProperties)

  //console.log("heads", detail)
  const [isEditMode, setIsEditMode] = useState(false)
  const [recordId, setRecordId] = useState(null)
  const [isLoading, setLoading] = useState(loading)

  useEffect(() => {
    // Fetch initial data
    dispatch(getGoodServicesSubHeadMasterList())

    // Check for edit mode
    if (location.pathname.includes("edit")) {
      const id = location.pathname.split(
        "/edit-goods-services-sub-head-master/"
      )[1]
      setRecordId(id)
      setIsEditMode(true)
      dispatch(getGoodServicesSubHeadMasterDetail(id))
    }
  }, [dispatch])

  const [users, setUsers] = useState([])
  const [serviceGoodsCategories, setServiceGoodsCategories] = useState([])

  useEffect(() => {
    // Define and execute data fetching functions
    const fetchUserData = async () => {
      try {
        const response = await getUsersAndGoodsServices()
        setUsers(response.data.users)
        setServiceGoodsCategories(response.data.proposal_types)
      } catch (error) {
        console.error("Error fetching users:", error)
      }
    }

    // Call both fetch functions
    fetchUserData()
  }, []) // Empty dependency array means this runs once on mount

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      head_id: (detail && detail.head_id) || "",
      subhead_text: (detail && detail.subhead_text) || "",
      good_service_id: (detail && detail.good_service_id) || "",
      is_active: Boolean(detail && detail.is_active) || false,
    },
    validationSchema: Yup.object({
      head_id: Yup.number().required("Please select a Head"),
      subhead_text: Yup.string().required("Please select a Sub-Head"),
      good_service_id: Yup.number().required("Please select Goods/Services"),
      is_active: Yup.boolean(),
    }),
    onSubmit: values => {
      if (isEditMode) {
        dispatch(updateGoodServicesSubHeadMaster(recordId, values, navigate))
      } else {
        dispatch(addNewGoodServicesSubHeadMaster(values, navigate))
      }
    },
  })

  return (
    <div className="page-content">
      {isLoading ? (
        <Spinners setLoading={setLoading} />
      ) : (
        <>
          <Row>
            <Col>
              <div className="d-flex gap-2 mb-3">
                <i
                  className="mdi mdi-arrow-left font-size-20"
                  style={{ color: "grey", marginRight: "15px" }}
                  onClick={() => navigate(-1)}
                />
                <span
                  style={{
                    color: "black",
                    fontWeight: "500",
                    fontSize: "18px",
                  }}
                >
                  {isEditMode
                    ? "Edit Goods/Services Sub-Head"
                    : "New Goods/Services Sub-Head"}
                </span>
              </div>
            </Col>
            <Col>
              <Breadcrumbs title="Settings / Master configuration / Goods & Services Sub-Head" />
            </Col>
          </Row>

          <Card>
            <CardBody>
              <Form onSubmit={validation.handleSubmit}>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>Head *</Label>
                      <Input
                        type="select"
                        name="head_id"
                        className="form-control"
                        onChange={(e)=>{handleHeadChange(e)}}
                        onBlur={validation.handleBlur}
                        value={validation.values.head_id}
                        invalid={
                          validation.touched.head_id &&
                          !!validation.errors.head_id
                        }
                      >
                        <option value="">Select Head</option>
                        {headList.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.head_text}
                          </option>
                        ))}
                      </Input>
                      {validation.touched.head_id &&
                        validation.errors.head_id && (
                          <FormFeedback>
                            {validation.errors.head_id}
                          </FormFeedback>
                        )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>Sub-Head *</Label>
                      <Input
                        type="text"
                        name="subhead_text"
                        className="form-control"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.subhead_text}
                        invalid={
                          validation.touched.subhead_text &&
                          !!validation.errors.subhead_text
                        }
                      />
                      {validation.touched.subhead_text &&
                        validation.errors.subhead_text && (
                          <FormFeedback>
                            {validation.errors.subhead_text}
                          </FormFeedback>
                        )}
                    </div>
                  </Col>

                  <Col md={12}>
                    <div className="mb-3">
                      <div className="form-check">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="is_active"
                          name="is_active"
                          checked={validation.values.is_active}
                          onChange={e =>
                            validation.setFieldValue(
                              "is_active",
                              e.target.checked
                            )
                          }
                        />
                        <Label className="form-check-label" htmlFor="is_active">
                          Is Active
                        </Label>
                      </div>
                    </div>
                  </Col>
                </Row>

                <div className="text-end">
                  <button type="submit" className="btn btn-primary w-md">
                    {isEditMode ? "Update" : "Submit"}
                  </button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  )
}

NewGoodServicesSubHeadMaster.propTypes = {
  router: PropTypes.object,
}

export default withRouter(NewGoodServicesSubHeadMaster)
